import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useFormContext } from "react-hook-form";
import { v4 as uuid } from "uuid";

import { Button, Loading } from "ui-library/atoms";
import { Box } from "ui-library/layouts";

import i18n from "core/i18n";
import { CancelRequestButton, InPageFormButtonsLayout } from "containers/inPageForm";
import { useHasRole } from "core/hooks";
import { Roles } from "enums";
import { FileItem, FileItemStatusEnum, Document } from "types";

import useStepDocuments from "../hooks/useStepDocuments";
import useDigisign from "../hooks/useDigisign";
import useAttachments from "../hooks/useAttachments";
import { SignatureType, VehicleSubjectChangeFormValues } from "../types/VehicleSubjectChangeFormValues";
import { isAllAttachmentsUploaded } from "../util";

import { AttachmentsSection } from "./Attachments";
import { RequestDocumentSignPhysical } from "./RequestDocumentSignPhysical";
import { RequestDocumentSignDigiSignOneTime } from "./RequestDocumentSignDigiSignOneTime";
import { RequestDocumentSignDigiSignAuthorized } from "./RequestDocumentSignDigiSignAuthorized";

export default function StepDocuments({ nextStep }: { nextStep: () => void }) {
    const { t } = i18n.useTranslation();
    const { idObject } = useParams();
    const { idChange, setValue, signatureType, uploadedFile, submit, getIsSubmitButtonDisabled, isDocumentsFetching, isSubmitting } =
        useStepDocuments(nextStep);

    const { getValues, watch } = useFormContext<VehicleSubjectChangeFormValues>();
    const changeId = getValues("idChange");
    const uploadedAttachments = watch("attachments") || {};

    const { attachments, isAttachmentsFetching } = useAttachments();

    const { handleDigitalSignatureVerify, digitalSignatureOnlineStatus } = useDigisign(idObject, changeId);

    useEffect(() => {
        handleDigitalSignatureVerify();
    }, [idObject, changeId]);

    // Preload the values for form from BE.
    useEffect(() => {
        attachments.forEach((attachment) => {
            const fileItems: FileItem[] = attachment.documents.map((doc: Document) => ({
                id: uuid(),
                uploadId: doc.id,
                fileName: doc.fileName,
                uploadStatus: FileItemStatusEnum.UPLOADED,
            }));

            setValue(`attachments.${attachment.type}`, fileItems);
        });
    }, [attachments]);

    const isAuthenticated = useHasRole(Roles.AUTHENTICATED);

    const isSignMissing =
        (signatureType === SignatureType.PHYSICAL && getIsSubmitButtonDisabled()) ||
        (signatureType === SignatureType.ELECTRONIC && isAuthenticated && digitalSignatureOnlineStatus !== "signed");

    const isSignOrAttachmentMissing = isSignMissing || !isAllAttachmentsUploaded(attachments, uploadedAttachments);

    return (
        <>
            <Box mb={8}>
                <Loading loading={isDocumentsFetching || isAttachmentsFetching}>
                    {attachments?.length > 0 && <AttachmentsSection attachments={attachments} />}

                    {signatureType === SignatureType.PHYSICAL && (
                        <RequestDocumentSignPhysical idChange={idChange} uploadedFile={uploadedFile} />
                    )}

                    {signatureType === SignatureType.ELECTRONIC &&
                        (isAuthenticated ? (
                            <RequestDocumentSignDigiSignAuthorized digitalSignatureOnlineStatus={digitalSignatureOnlineStatus} />
                        ) : (
                            <RequestDocumentSignDigiSignOneTime />
                        ))}

                    <InPageFormButtonsLayout
                        leftButton={<CancelRequestButton changeSheetId={idChange} />}
                        rightButton={
                            <Button variant="contained" color="blue" onClick={submit} disabled={isSubmitting || isSignOrAttachmentMissing}>
                                {isSubmitting ? <Loading loading /> : t("change.finalizeRequest")}
                            </Button>
                        }
                    />
                </Loading>
            </Box>
        </>
    );
}
